import { useState, useEffect, useMemo, useRef, type ReactNode } from 'react';
import { collection, query, where, onSnapshot, doc, runTransaction } from 'firebase/firestore';
import { getClientDb } from '../firebase/clientApp';
import { useAuth } from '../context/AuthContext';
import { logActivity } from '../utils/activity';
import AppLayout from '../components/AppLayout';
import { InvoicePreviewModal } from './Purchase';
import { HeaderSearch, StatChips } from '../components/HeaderControls';
import RecordPaymentModal from '../components/RecordPaymentModal';
import { Button, Modal, ConfirmModal, FormField } from '../components/ui';
import { fmtMoney, fmtCompact } from '../utils/currency';
import { useSettings } from '../hooks/useSettings';
import { buildEntry, postEntry, entryId, linesApPayment } from '../utils/ledger';
import { dueGroupOutstanding, shiftDueGroupPaid } from '../utils/dueGroups';
import type { PurchaseInvoice, PurchasePayment, PaymentStatus, BankAccount, Entity, DueGroup } from '../types';
import { invoiceNoMatches, shortInvoiceNo } from '../utils/invoiceNo';
import { useColumnFilters, type FilterColumn } from '../hooks/useColumnFilters';
import { ColumnFilterButton, ColumnFilterChips } from '../components/ColumnFilter';
import { usePaging } from '../hooks/usePaging';
import { useFitHeight } from '../hooks/useFitHeight';
import { Paging } from '../components/ui/Paging';

// ── Helpers ───────────────────────────────────────────────────────────────────

function daysFromToday(dateStr: string): number {
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const due   = new Date(dateStr + 'T00:00:00');
  return Math.floor((today.getTime() - due.getTime()) / 86_400_000);
}
function outstanding(inv: PurchaseInvoice): number {
  // creditedUSD = purchase returns + linked debit notes against this invoice (Phase B1)
  return Math.max(0, inv.totalAmountUSD - (inv.paidUSD ?? 0) - (inv.creditedUSD ?? 0));
}

// ── Payable rows — flattens each invoice into one row per due date ─────────
// An 'average'-mode (or legacy) invoice is always exactly one row, byte-for-
// byte today's shape. A 'separate'-mode invoice becomes one row per
// DueGroup, each carrying only that group's own amount/outstanding/status —
// this is what lets AP's list/calendar/KPIs show "what's owed on what date"
// instead of one lump sum per invoice.
interface ApRow {
  key: string;
  invoice: PurchaseInvoice;
  group: DueGroup | null;
  dueDate: string;
  amountUSD: number;
  outstanding: number;
  status: PaymentStatus;
}
function invoiceToRows(inv: PurchaseInvoice): ApRow[] {
  if (inv.dueDateMode === 'separate' && inv.dueGroups && inv.dueGroups.length > 0) {
    return inv.dueGroups.map(g => ({
      key: `${inv.id}_${g.id}`, invoice: inv, group: g,
      dueDate: g.dueDate, amountUSD: g.amountUSD,
      outstanding: dueGroupOutstanding(g), status: g.paymentStatus,
    }));
  }
  return [{
    key: inv.id, invoice: inv, group: null,
    dueDate: inv.dueDate, amountUSD: inv.totalAmountUSD,
    outstanding: outstanding(inv), status: inv.paymentStatus,
  }];
}
// Invoice/Lot No is stored in full, but shown here as just the prefix + last
// 4 digits to keep the column compact. Keeping the prefix matters — a bare
// last-4 truncation collapses "PC-2026-0001" and "PL-2026-0001" into the
// same "0001", making certified and loose lots indistinguishable.
function lotNoDisplay(invoiceNo: string) {
  if (!invoiceNo) return '—';
  const m = invoiceNo.match(/^([A-Za-z]+)-\d+-(\d+)$/);
  return m ? `${m[1]}-${m[2].slice(-4)}` : invoiceNo.slice(-4);
}
// Every broker on an invoice: the old invoice-level one plus each line's
// Broker 1 / 2, once each (same rule as the Sales list's Broker column).
function invoiceBrokerIds(inv: { brokerEntityId?: string; lineItems?: { broker1EntityId?: string; broker2EntityId?: string }[] }): string[] {
  const ids = [inv.brokerEntityId, ...(inv.lineItems ?? []).flatMap(li => [li.broker1EntityId, li.broker2EntityId])];
  return [...new Set(ids.filter((x): x is string => !!x))];
}

function todayISO() {
  const d = new Date();   // local date, not UTC (see CLAUDE.md: Date-only strings)
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

// The Due Date column filter groups payments by how urgent they are.
const DUE_ORDER = ['Overdue', 'Due this week', 'Later', 'Paid'];
function dueBucket(r: { status: PaymentStatus; dueDate: string }): string {
  if (r.status === 'paid') return 'Paid';
  const days = daysFromToday(r.dueDate);
  return days > 0 ? 'Overdue' : days >= -7 ? 'Due this week' : 'Later';
}
function statusLabel(status: PaymentStatus): string {
  return status === 'paid' ? 'Paid' : status === 'partial' ? 'Partly paid' : 'Unpaid';
}
// The list opens on what's still owed; Paid is one tick away in the Status filter.
const DEFAULT_STATUS = ['Unpaid', 'Partly paid'];
function onDefaultFilters(selected: Record<string, string[]>): boolean {
  const keys = Object.keys(selected);
  return keys.length === 1 && keys[0] === 'status'
    && selected.status.length === DEFAULT_STATUS.length && DEFAULT_STATUS.every(v => selected.status.includes(v));
}

function buildCalDays(year: number, month: number): (Date | null)[] {
  const first  = new Date(year, month, 1);
  const days: (Date | null)[] = [];
  for (let i = 0; i < first.getDay(); i++) days.push(null);
  const last = new Date(year, month + 1, 0).getDate();
  for (let d = 1; d <= last; d++) days.push(new Date(year, month, d));
  while (days.length % 7 !== 0) days.push(null);
  return days;
}

const MONTH_NAMES = ['January','February','March','April','May','June',
                     'July','August','September','October','November','December'];
const DAY_NAMES   = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];


// ── Icons ─────────────────────────────────────────────────────────────────────

function IconPay() {
  return <svg width="13" height="13" viewBox="0 0 14 14" fill="none">
    <rect x="1" y="3" width="12" height="8" rx="1.5" stroke="currentColor" strokeWidth="1.4" />
    <path d="M1 6h12" stroke="currentColor" strokeWidth="1.4" />
    <circle cx="4" cy="9" r="1" fill="currentColor" />
  </svg>;
}
function IconList() {
  return <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
    <path d="M2 3.5h10M2 7h10M2 10.5h10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
  </svg>;
}
function IconCalendar() {
  return <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
    <rect x="1" y="2.5" width="12" height="10" rx="1.5" stroke="currentColor" strokeWidth="1.4" />
    <path d="M1 6h12M4.5 1v3M9.5 1v3" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
  </svg>;
}
function IconCash({ size = 13 }: { size?: number }) {
  return <svg width={size} height={size} viewBox="0 0 14 14" fill="none">
    <rect x="1" y="3.5" width="12" height="7" rx="1.3" stroke="currentColor" strokeWidth="1.3" />
    <circle cx="7" cy="7" r="1.6" stroke="currentColor" strokeWidth="1.2" />
  </svg>;
}
function IconBank({ size = 13 }: { size?: number }) {
  return <svg width={size} height={size} viewBox="0 0 14 14" fill="none">
    <rect x="1.5" y="6.5" width="11" height="6" rx="0.8" stroke="currentColor" strokeWidth="1.3" />
    <path d="M1.5 6.5L7 2l5.5 4.5" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round" />
    <path d="M3.7 9v2.2M7 9v2.2M10.3 9v2.2" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
  </svg>;
}
function IconCheckCircle({ size = 32 }: { size?: number }) {
  return <svg width={size} height={size} viewBox="0 0 32 32" fill="none">
    <circle cx="16" cy="16" r="13" stroke="currentColor" strokeWidth="2" />
    <path d="M10.5 16.5l3.5 3.5 7.5-8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>;
}

// ── PayBadge ──────────────────────────────────────────────────────────────────

function PayBadge({ inv }: { inv: PurchaseInvoice }) {
  const status = inv.paymentStatus ?? 'unpaid';
  const paid   = inv.paidUSD ?? 0;
  const pct    = inv.totalAmountUSD > 0 ? Math.round((paid / inv.totalAmountUSD) * 100) : 0;
  if (status === 'paid') return (
    <span className="badge" style={{ background: 'var(--success-bg)', color: 'var(--success)', border: '1px solid var(--success)' }}>Paid</span>
  );
  if (status === 'partial') return (
    <span className="badge" style={{ background: 'var(--warning-bg)', color: '#c2410c', border: '1px solid var(--warning)' }}>{pct}% paid</span>
  );
  return (
    <span className="badge" style={{ background: 'var(--danger-bg)', color: 'var(--danger)', border: '1px solid var(--danger)' }}>Unpaid</span>
  );
}

// ── Detail modal header facts — Company Name / Lot No, matching Purchase & Inventory ──

// Thin vertical rule between facts inside a FactPanel.

// Groups the header's fact cluster into one floating pill-shaped panel —
// without this, several label/value pairs sitting bare next to the title
// just read as scattered text with no visual relationship to each other.
// Rounded ends + a soft shadow give it its own identity instead of looking
// like just another rectangular box on the page.

// Compact label-over-value pair for the header's right-hand fact cluster —
// small enough that several fit on one line next to the title without a box.

// The one identifier worth calling out from the rest — same highlighted pill
// used for Lot No everywhere else in the app (the invoice list, Inventory's
// SKU column, etc.), so it's instantly recognizable rather than blending in.

// Small uppercase section label — reused above each block in the detail
// modal (Payment Summary / Line Items / Payment History) so every section
// header reads consistently instead of some being plain text and others not.
function SectionLabel({ children }: { children: ReactNode }) {
  return (
    <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-2)', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
      {children}
    </div>
  );
}

// ── Line items sub-table (used inside the detail modal) ───────────────────────


// ── Invoice detail modal ──────────────────────────────────────────────────────

function InvoiceDetailModal({
  invoice, onClose, onPay, entities,
}: {
  invoice: PurchaseInvoice | null;
  onClose: () => void;
  onPay:   (inv: PurchaseInvoice) => void;
  entities: Entity[];
  /** Highlight this DueGroup's row in the Due Dates table below — set when
   *  opened from one specific due-date row in AP's flattened list/calendar. */
  focusGroupId?: string;
}) {
  const { user, business } = useAuth();
  const settings = useSettings();
  const money = (v: number) => fmtMoney(v, settings.currency);

  // ── Payment list ──────────────────────────────────────────────────────────
  const [payments,    setPayments]    = useState<PurchasePayment[]>([]);
  const [paysLoading, setPaysLoading] = useState(false);

  useEffect(() => {
    if (!invoice) { setPayments([]); setPaysLoading(false); return; }
    const db = getClientDb();
    if (!db) { setPaysLoading(false); return; }
    setPaysLoading(true);
    const unsub = onSnapshot(
      collection(db, 'invoices', invoice.id, 'payments'),
      snap => {
        const pays = snap.docs
          .map(d => ({ id: d.id, ...d.data() } as PurchasePayment))
          .sort((a, b) => (a.paidDate ?? '').localeCompare(b.paidDate ?? ''));
        setPayments(pays);
        setPaysLoading(false);
      },
      err => { console.error('Payment history error:', err); setPaysLoading(false); },
    );
    return unsub;
  }, [invoice?.id]);

  // ── Edit state ────────────────────────────────────────────────────────────
  const [editingPay,  setEditingPay]  = useState<PurchasePayment | null>(null);
  const [editForm,    setEditForm]    = useState({ amount: '', paidDate: '', referenceNo: '', notes: '' });
  const [editSaving,  setEditSaving]  = useState(false);
  const [editError,   setEditError]   = useState('');

  function openEdit(pay: PurchasePayment) {
    setEditingPay(pay);
    setEditForm({
      amount:      String(payAmt(pay)),
      paidDate:    pay.paidDate ?? todayISO(),
      referenceNo: pay.referenceNo ?? '',
      notes:       pay.notes      ?? '',
    });
    setEditError('');
  }

  async function handleEditSave() {
    if (!editingPay || !invoice || !business) return;
    const newAmt = parseFloat(editForm.amount);
    if (isNaN(newAmt) || newAmt <= 0) { setEditError('Enter a valid amount.'); return; }
    if (!editForm.paidDate) { setEditError('Select a date.'); return; }

    setEditSaving(true); setEditError('');
    const db = getClientDb();
    if (!db) { setEditError('Database not connected.'); setEditSaving(false); return; }

    const oldAmt   = payAmt(editingPay);
    const diff     = newAmt - oldAmt;                        // positive = paid more
    const newPaid  = Math.max(0, (invoice.paidUSD ?? 0) + diff);
    const newStatus: PaymentStatus =
      newPaid + (invoice.creditedUSD ?? 0) >= invoice.totalAmountUSD - 0.005 ? 'paid'
      : newPaid > 0 ? 'partial' : 'unpaid';

    // A payment recorded from a differently-denominated bank account carries
    // its own converted `bankAmount`, proportional to `amount` via the rate
    // stored at record time (see RecordPaymentModal.tsx) — the bank
    // account's own balance/transaction must move by THAT figure's delta,
    // not the raw base-currency `diff`, or editing a converted payment
    // silently corrupts the account's balance. Unset for an ordinary
    // same-currency payment, where bankDiff === diff exactly (unchanged
    // behavior from before this fix).
    const newBankAmt = editingPay.bankCurrency && editingPay.exchangeRate
      ? newAmt * editingPay.exchangeRate
      : newAmt;
    const oldBankAmt = editingPay.bankAmount ?? oldAmt;
    const bankDiff    = newBankAmt - oldBankAmt;

    try {
      await runTransaction(db, async tx => {
        const payRef = doc(db, 'invoices', invoice.id, 'payments', editingPay.id);
        const invRef = doc(db, 'invoices', invoice.id);
        const accRef = (editingPay.bankAccountId && bankDiff !== 0) ? doc(db, 'bankAccounts', editingPay.bankAccountId) : null;

        // All reads before any writes — Firestore transactions require it.
        const accSnap = accRef ? await tx.get(accRef) : null;
        // A payment against one due date of a 'separate'-mode invoice must
        // move that group too, or the groups and the invoice drift apart.
        const groupSnap = editingPay.dueGroupId ? await tx.get(invRef) : null;
        const groupUpdate = groupSnap?.exists()
          ? shiftDueGroupPaid((groupSnap.data() as PurchaseInvoice).dueGroups ?? [], editingPay.dueGroupId!, diff)
          : null;

        tx.update(payRef, {
          amount:     newAmt,
          bankAmount: editingPay.bankCurrency ? newBankAmt : null,
          paidDate:   editForm.paidDate,
          referenceNo: editForm.referenceNo.trim() || null,
          notes:       editForm.notes.trim()        || null,
        });
        logActivity(tx, db, business.id, user, {
          module: 'payment', action: 'edit', recordId: invoice.id, recordNo: invoice.invoiceNo, partyId: invoice.fromEntityId,
          amountUSD: newAmt, prevAmountUSD: oldAmt, note: editingPay.bankAccountName,
        });

        tx.update(invRef, groupUpdate
          ? { ...groupUpdate, updatedAt: new Date().toISOString() }
          : { paidUSD: newPaid, paymentStatus: newStatus, updatedAt: new Date().toISOString() });

        // Cascade to bank account if linked
        if (accRef && accSnap?.exists()) {
          const cur = (accSnap.data() as BankAccount).currentBalance;
          tx.update(accRef, { currentBalance: cur - bankDiff });
        }

        // Cascade to bank transaction if linked
        const btId = (editingPay as any).bankTransactionId as string | undefined;
        if (btId && bankDiff !== 0) {
          tx.update(doc(db, 'bankTransactions', btId), { amount: newBankAmt });
        }

        // Cascade to the ledger — overwrite this payment's entry in place.
        if (editingPay.bankAccountId) {
          postEntry(tx, db, buildEntry({
            businessId: business.id, sourceType: 'ap_payment', sourceId: editingPay.id,
            sourceCollection: `invoices/${invoice.id}/payments`,
            date: editForm.paidDate, currency: editingPay.currency ?? 'USD',
            createdBy: user?.id ?? editingPay.createdBy ?? 'system',
            note: `Payment for ${invoice.invoiceNo}`,
            lines: linesApPayment(newAmt, invoice.fromEntityId, editingPay.bankAccountId),
          }), entryId('ap_payment', editingPay.id));
        }
      });

      setEditingPay(null);
    } catch (e) {
      setEditError(e instanceof Error ? e.message : 'Failed to save.');
    }
    setEditSaving(false);
  }

  // ── Delete state ──────────────────────────────────────────────────────────
  const [deletingPay,  setDeletingPay]  = useState<PurchasePayment | null>(null);
  const [deleteSaving, setDeleteSaving] = useState(false);
  const [deleteError,  setDeleteError]  = useState('');

  async function handleDeletePayment() {
    if (!deletingPay || !invoice || !business) return;
    setDeleteSaving(true); setDeleteError('');
    const db = getClientDb();
    if (!db) { setDeleteError('Database not connected.'); setDeleteSaving(false); return; }

    const amt      = payAmt(deletingPay);
    // The refund must be in the bank account's own currency, not the base
    // amount, for a payment that was converted at record time — falls back
    // to `amt` for an ordinary same-currency payment (unchanged behavior).
    const bankAmt  = deletingPay.bankAmount ?? amt;
    const newPaid  = Math.max(0, (invoice.paidUSD ?? 0) - amt);
    const newStatus: PaymentStatus =
      newPaid + (invoice.creditedUSD ?? 0) >= invoice.totalAmountUSD - 0.005 ? 'paid'
      : newPaid > 0 ? 'partial' : 'unpaid';

    try {
      await runTransaction(db, async tx => {
        const payRef = doc(db, 'invoices', invoice.id, 'payments', deletingPay.id);
        const invRef = doc(db, 'invoices', invoice.id);
        const accRef = deletingPay.bankAccountId ? doc(db, 'bankAccounts', deletingPay.bankAccountId) : null;

        // All reads before any writes — Firestore transactions require it.
        const accSnap = accRef ? await tx.get(accRef) : null;
        const groupSnap = deletingPay.dueGroupId ? await tx.get(invRef) : null;
        const groupUpdate = groupSnap?.exists()
          ? shiftDueGroupPaid((groupSnap.data() as PurchaseInvoice).dueGroups ?? [], deletingPay.dueGroupId!, -amt)
          : null;

        tx.delete(payRef);
        logActivity(tx, db, business.id, user, {
          module: 'payment', action: 'delete', recordId: invoice.id, recordNo: invoice.invoiceNo, partyId: invoice.fromEntityId,
          amountUSD: amt, note: deletingPay.bankAccountName,
        });
        tx.update(invRef, groupUpdate
          ? { ...groupUpdate, updatedAt: new Date().toISOString() }
          : { paidUSD: newPaid, paymentStatus: newStatus, updatedAt: new Date().toISOString() });

        // Refund bank account balance
        if (accRef && accSnap?.exists()) {
          const cur = (accSnap.data() as BankAccount).currentBalance;
          tx.update(accRef, { currentBalance: cur + bankAmt });
        }

        // Delete the linked bank transaction
        const btId = (deletingPay as any).bankTransactionId as string | undefined;
        if (btId) tx.delete(doc(db, 'bankTransactions', btId));

        // Remove this payment's ledger entry.
        tx.delete(doc(db, 'journalEntries', entryId('ap_payment', deletingPay.id)));
      });

      setDeletingPay(null);
    } catch (e) {
      setDeleteError(e instanceof Error ? e.message : 'Failed to delete.');
    }
    setDeleteSaving(false);
  }

  // ── Helpers ───────────────────────────────────────────────────────────────
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  function payAmt(p: PurchasePayment): number { return p.amount ?? (p as any).amountUSD ?? 0; }
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  function payAccName(p: PurchasePayment): string { return p.bankAccountName ?? (p as any).paymentMethod ?? '—'; }

  if (!invoice) return null;

  const owed       = outstanding(invoice);
  const paid       = invoice.paidUSD ?? 0;
  const days       = daysFromToday(invoice.dueDate);
  const isOverdue  = days > 0;
  const dueSoon    = !isOverdue && days >= -7;
  const dueColor   = isOverdue ? 'var(--danger)' : dueSoon ? '#c2410c' : 'var(--text-1)';
  const dueLabel   = isOverdue ? `${days}d overdue` : days === 0 ? 'Due today' : `Due in ${Math.abs(days)}d`;
  const totalPaidFromRecords = payments.reduce((s, p) => s + payAmt(p), 0);

  // suppress unused warning — user is available for future use
  void user;

  return (
    <>
    {/* Same popup as the Purchase page (header, line items, due dates,
        expenses, broker), with this page's payment parts added below. Discounts (Apply / Edit /
        Delete) live in the popup's own Discounts list. */}
    <InvoicePreviewModal
      invoice={invoice}
      entityName={id => entities.find(e => e.id === id)?.companyName ?? '—'}
      onClose={onClose}
      onPay={onPay}
      payLabel="Record Payment"
      canEditDiscounts
    >
      <div style={{ marginTop: 24, paddingTop: 20, borderTop: '1px solid var(--border)' }}>
      {/* ── Payment summary bar ── */}
        <SectionLabel>Payment Summary</SectionLabel>
        {(() => {
          const tiles: { label: string; val: number | null; special?: boolean; color: string }[] = [
            { label: 'Invoice Total', val: invoice.totalAmountUSD, color: 'var(--text-1)'   },
            { label: 'Paid',          val: paid,                   color: 'var(--success)'  },
            ...((invoice.discountUSD ?? 0) > 0
              ? [{ label: 'Discount', val: invoice.discountUSD ?? 0, color: 'var(--success)' }]
              : []),
            { label: 'Outstanding',   val: owed,                   color: owed > 0 ? 'var(--danger)' : 'var(--text-3)' },
            { label: 'Due Date',      val: null, special: true,    color: dueColor          },
          ];
          return (
            <div style={{
              display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(110px, 1fr))', gap: 1,
              border: '1px solid var(--border)', borderRadius: 'var(--r-sm)',
              overflow: 'hidden', marginBottom: 20,
            }}>
              {tiles.map(({ label, val, special, color }) => (
                <div key={label} style={{ background: 'var(--surface-2)', padding: '12px 14px' }}>
                  <div style={{ fontSize: 11, color: 'var(--text-3)', fontWeight: 500, marginBottom: 4 }}>{label}</div>
                  <div style={{ fontWeight: 700, fontFamily: !special ? 'var(--font-num)' : undefined, fontSize: 14, color }}>
                    {special ? (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <span style={{ fontSize: 13 }}>{new Date(invoice.dueDate + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
                        <span style={{ fontSize: 11, fontWeight: 600 }}>{dueLabel}</span>
                      </div>
                    ) : val !== null ? money(val) : <span style={{ opacity: 0.4 }}>—</span>}
                  </div>
                </div>
              ))}
            </div>
          );
        })()}
      </div>

      {/* ── Payment History ── */}
      <div style={{ marginTop: 24, paddingTop: 20, borderTop: '1px solid var(--border)' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-2)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            Payment History
          </div>
          {payments.length > 0 && (
            <div style={{ fontSize: 12, color: 'var(--text-3)' }}>
              {payments.length} payment{payments.length !== 1 ? 's' : ''} ·{' '}
              <span style={{ fontWeight: 700, color: 'var(--success)' }}>{money(totalPaidFromRecords)} total</span>
            </div>
          )}
        </div>

        {paysLoading ? (
          <div className="skeleton" style={{ height: 60, borderRadius: 8 }} />
        ) : payments.length === 0 ? (
          <div style={{ fontSize: 13, color: 'var(--text-3)', padding: '10px 0' }}>No payments recorded yet.</div>
        ) : (
          <div style={{ overflowX: 'auto', borderRadius: 'var(--r-sm)', border: '1px solid var(--border)' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
              <thead>
                <tr style={{ background: 'var(--surface-2)' }}>
                  {['Date', 'Amount', 'Paid From', 'Reference', 'Notes', ''].map((h, i) => (
                    <th key={i} style={{ padding: '7px 12px', textAlign: i === 5 ? 'right' : 'left', fontWeight: 600, color: 'var(--text-3)', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em', borderBottom: '1px solid var(--border)', whiteSpace: 'nowrap' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {payments.map((pay, idx) => {
                  const amt     = payAmt(pay);
                  const accName = payAccName(pay);
                  const cur     = pay.currency ?? 'USD';
                  const dateStr = pay.paidDate
                    ? new Date(pay.paidDate + 'T00:00:00').toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' })
                    : '—';
                  return (
                    <tr key={pay.id ?? idx} style={{ background: idx % 2 === 0 ? 'var(--surface)' : 'var(--surface-2)' }}>
                      <td style={{ padding: '8px 12px', fontFamily: 'var(--font-num)', color: 'var(--text-2)', whiteSpace: 'nowrap' }}>{dateStr}</td>
                      <td style={{ padding: '8px 12px', fontFamily: 'var(--font-num)', fontWeight: 700, color: 'var(--success)', whiteSpace: 'nowrap' }}>{fmtMoney(amt, cur)}</td>
                      <td style={{ padding: '8px 12px' }}>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, color: 'var(--text-3)' }}>
                          {accName.toLowerCase().includes('cash') ? <IconCash /> : <IconBank />}
                          <span style={{ fontWeight: 500, color: 'var(--text-1)' }}>{accName}</span>
                          {pay.currency && <span style={{ fontSize: 10, color: 'var(--text-3)', padding: '1px 4px', background: 'var(--surface-2)', border: '1px solid var(--border)', borderRadius: 3 }}>{pay.currency}</span>}
                        </span>
                      </td>
                      <td style={{ padding: '8px 12px', fontFamily: 'var(--font-num)', color: pay.referenceNo ? 'var(--text-1)' : 'var(--text-3)' }}>{pay.referenceNo || '—'}</td>
                      <td style={{ padding: '8px 12px', color: 'var(--text-3)' }}>{pay.notes || '—'}</td>
                      <td style={{ padding: '8px 10px', textAlign: 'right', whiteSpace: 'nowrap' }}>
                        <div className="action-row" style={{ justifyContent: 'flex-end' }}>
                          <button
                            title="Edit"
                            onClick={() => openEdit(pay)}
                            style={{ padding: '4px 8px', border: '1.5px solid var(--border)', borderRadius: 'var(--r-sm)', background: 'var(--surface)', color: 'var(--text-2)', cursor: 'pointer', fontSize: 11, fontWeight: 600 }}
                          >Edit</button>
                          <button
                            title="Delete"
                            onClick={() => { setDeletingPay(pay); setDeleteError(''); }}
                            style={{ padding: '4px 8px', border: '1.5px solid color-mix(in srgb, var(--danger) 35%, transparent)', borderRadius: 'var(--r-sm)', background: 'var(--danger-bg)', color: 'var(--danger)', cursor: 'pointer', fontSize: 11, fontWeight: 600 }}
                          >Delete</button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr style={{ background: 'var(--surface-2)', borderTop: '2px solid var(--border)' }}>
                  <td style={{ padding: '7px 12px', fontWeight: 600, fontSize: 11, color: 'var(--text-3)' }}>Total</td>
                  <td style={{ padding: '7px 12px', fontFamily: 'var(--font-num)', fontWeight: 700, color: 'var(--success)' }}>{money(totalPaidFromRecords)}</td>
                  <td colSpan={4} />
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </div>
    </InvoicePreviewModal>

    {/* ── Edit payment modal ── */}
    <Modal
      open={!!editingPay}
      onClose={() => setEditingPay(null)}
      title="Edit Payment"
      subtitle={editingPay ? `${payAccName(editingPay)} · ${editingPay.paidDate ?? ''}` : ''}
      size="sm"
      footer={
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, width: '100%' }}>
          {editError && <div className="msg msg-error" style={{ margin: 0 }}>{editError}</div>}
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <Button variant="secondary" onClick={() => setEditingPay(null)} disabled={editSaving}>Cancel</Button>
            <Button variant="primary" onClick={handleEditSave} loading={editSaving}>Save Changes</Button>
          </div>
        </div>
      }
    >
      <div className="form-row-2">
        <FormField label={`Amount (${editingPay?.currency ?? 'USD'})`} required>
          <input
            type="number" step="0.01" min="0.01"
            value={editForm.amount}
            onChange={e => setEditForm(f => ({ ...f, amount: e.target.value }))}
            autoFocus
          />
        </FormField>
        <FormField label="Payment Date" required>
          <input
            type="date"
            value={editForm.paidDate}
            onChange={e => setEditForm(f => ({ ...f, paidDate: e.target.value }))}
          />
        </FormField>
      </div>
      <FormField label="Reference No.">
        <input
          value={editForm.referenceNo}
          onChange={e => setEditForm(f => ({ ...f, referenceNo: e.target.value }))}
          placeholder="Bank ref, cheque no., etc."
          style={{ fontFamily: 'var(--font-num)' }}
        />
      </FormField>
      <FormField label="Notes (optional)">
        <textarea
          value={editForm.notes}
          onChange={e => setEditForm(f => ({ ...f, notes: e.target.value }))}
          style={{ minHeight: 52 }}
        />
      </FormField>
    </Modal>

    {/* ── Delete confirm ── */}
    <ConfirmModal
      open={!!deletingPay}
      onClose={() => setDeletingPay(null)}
      onConfirm={handleDeletePayment}
      title="Delete Payment"
      message={
        deletingPay
          ? <>Delete payment of <strong>{money(payAmt(deletingPay))}</strong> on {deletingPay.paidDate}?
              This will reverse the invoice balance and refund the bank account.</>
          : null
      }
      confirmLabel="Delete Payment"
      loading={deleteSaving}
      error={deleteError}
    />
    </>
  );
}

// ── Calendar view ─────────────────────────────────────────────────────────────

function CalendarView({
  rows, entities, calYear, calMonth,
  onPrevMonth, onNextMonth, onSelect,
}: {
  rows:        ApRow[];
  entities:    Entity[];
  calYear:     number;
  calMonth:    number;
  onPrevMonth: () => void;
  onNextMonth: () => void;
  onSelect:    (row: ApRow) => void;
}) {
  const settings = useSettings();
  const money = (v: number) => fmtMoney(v, settings.currency);
  const moneyCompact = (v: number) => fmtCompact(v, settings.currency);
  const today    = todayISO();
  const days     = buildCalDays(calYear, calMonth);
  const now      = new Date();
  const isFuture = calYear > now.getFullYear() ||
    (calYear === now.getFullYear() && calMonth > now.getMonth());

  const byDate = useMemo(() => {
    const map: Record<string, ApRow[]> = {};
    rows.forEach(row => {
      const d = new Date(row.dueDate + 'T00:00:00');
      if (d.getFullYear() === calYear && d.getMonth() === calMonth) {
        if (!map[row.dueDate]) map[row.dueDate] = [];
        map[row.dueDate].push(row);
      }
    });
    return map;
  }, [rows, calYear, calMonth]);

  function entityName(id: string) {
    return entities.find(e => e.id === id)?.companyName ?? '—';
  }

  const monthTotal = useMemo(() =>
    Object.values(byDate).flat().reduce((s, row) => s + row.outstanding, 0),
  [byDate]);

  return (
    <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
      {/* Calendar header */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 12,
        padding: '14px 18px', borderBottom: '1px solid var(--border)',
        flexWrap: 'wrap',
      }}>
        {/* Month navigator — matches Purchase page style */}
        <div style={{
          display: 'inline-flex', alignItems: 'center',
          background: 'var(--surface)', border: '1.5px solid var(--border)',
          borderRadius: 'var(--r-sm)', overflow: 'hidden', boxShadow: 'var(--shadow-xs)',
        }}>
          <button
            onClick={onPrevMonth}
            style={{ padding: '6px 11px', border: 'none', borderRight: '1px solid var(--border)', background: 'transparent', cursor: 'pointer', color: 'var(--text-2)', fontSize: 16, lineHeight: 1, transition: 'background 0.12s', fontFamily: 'inherit' }}
            onMouseEnter={e => (e.currentTarget.style.background = 'var(--surface-2)')}
            onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
          >‹</button>
          <span style={{ padding: '6px 14px', fontWeight: 600, fontSize: 13, width: 150, boxSizing: 'border-box', textAlign: 'center', whiteSpace: 'nowrap', color: 'var(--text-1)', userSelect: 'none' }}>
            {MONTH_NAMES[calMonth]} {calYear}
          </span>
          <button
            onClick={onNextMonth}
            disabled={isFuture}
            style={{ padding: '6px 11px', border: 'none', borderLeft: '1px solid var(--border)', background: 'transparent', cursor: isFuture ? 'not-allowed' : 'pointer', color: isFuture ? 'var(--text-3)' : 'var(--text-2)', fontSize: 16, lineHeight: 1, opacity: isFuture ? 0.4 : 1, transition: 'background 0.12s', fontFamily: 'inherit' }}
            onMouseEnter={e => { if (!isFuture) e.currentTarget.style.background = 'var(--surface-2)'; }}
            onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
          >›</button>
        </div>

        {/* Month summary */}
        {monthTotal > 0 && (
          <div style={{ fontSize: 13, color: 'var(--text-3)' }}>
            <span style={{ fontWeight: 700, color: 'var(--danger)', fontFamily: 'var(--font-num)' }}>{money(monthTotal)}</span>
            {' '}<span style={{ color: 'var(--text-3)' }}>({Object.values(byDate).flat().length} invoice{Object.values(byDate).flat().length !== 1 ? 's' : ''})</span>
          </div>
        )}

        {/* Legend */}
        <div style={{ display: 'flex', gap: 12, marginLeft: 'auto', flexWrap: 'wrap' }}>
          {[
            { color: 'var(--danger)',  bg: '#fef2f2', border: '#fca5a5', label: 'Overdue' },
            { color: '#c2410c',        bg: '#fff7ed', border: '#fdba74', label: 'Due soon (≤7d)' },
            { color: 'var(--primary)', bg: 'var(--primary-light)', border: 'rgba(67,97,238,0.25)', label: 'Upcoming' },
          ].map(({ bg, border, label }) => (
            <div key={label} style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 11, color: 'var(--text-3)' }}>
              <span style={{ width: 10, height: 10, borderRadius: 3, background: bg, border: `1.5px solid ${border}`, flexShrink: 0, display: 'inline-block' }} />
              {label}
            </div>
          ))}
        </div>
      </div>

      {/* Day-of-week header row */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', background: 'var(--surface-2)', borderBottom: '1px solid var(--border)' }}>
        {DAY_NAMES.map((d, i) => (
          <div key={d} style={{
            padding: '5px 0', textAlign: 'center',
            fontSize: 10, fontWeight: 600, color: i === 0 || i === 6 ? 'var(--text-3)' : 'var(--text-2)',
            textTransform: 'uppercase', letterSpacing: '0.06em',
          }}>{d}</div>
        ))}
      </div>

      {/* Day cells grid */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)' }}>
        {days.map((day, i) => {
          if (!day) {
            return (
              <div key={`empty-${i}`} style={{
                minHeight: 64,
                borderRight: (i + 1) % 7 === 0 ? 'none' : '1px solid var(--border-2)',
                borderBottom: i < days.length - 7 ? '1px solid var(--border-2)' : 'none',
                background: 'var(--surface-2)',
              }} />
            );
          }

          // Local date (toISOString put each invoice a day late ahead of UTC).
          const dStr      = `${day.getFullYear()}-${String(day.getMonth() + 1).padStart(2, '0')}-${String(day.getDate()).padStart(2, '0')}`;
          const isToday   = dStr === today;
          const isPast    = dStr < today;
          const dayRows   = byDate[dStr] ?? [];
          const shown     = dayRows.slice(0, 2);
          const overflow  = dayRows.length - 2;
          const isWeekend = day.getDay() === 0 || day.getDay() === 6;
          const dayTotal  = dayRows.reduce((s, row) => s + row.outstanding, 0);

          return (
            <div key={dStr} style={{
              minHeight: 64, padding: '5px 5px',
              borderRight: (i + 1) % 7 === 0 ? 'none' : '1px solid var(--border-2)',
              borderBottom: i < days.length - 7 ? '1px solid var(--border-2)' : 'none',
              background: isToday
                ? 'rgba(67,97,238,0.04)'
                : isWeekend ? 'rgba(148,163,184,0.04)'
                : 'var(--surface)',
              transition: 'background 0.1s',
            }}>
              {/* Day number + total */}
              <div style={{ marginBottom: 3, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <span style={{
                  width: 20, height: 20, borderRadius: '50%',
                  background: isToday ? 'var(--primary)' : 'transparent',
                  color: isToday ? '#fff' : isPast ? 'var(--text-3)' : isWeekend ? 'var(--text-3)' : 'var(--text-2)',
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 11, fontWeight: isToday ? 700 : 500, flexShrink: 0,
                }}>{day.getDate()}</span>
                {dayRows.length > 0 && (
                  <span style={{
                    fontSize: 9, fontWeight: 700, fontFamily: 'var(--font-num)',
                    color: isPast ? 'var(--danger)' : 'var(--primary)',
                    flexShrink: 0,
                  }}>{moneyCompact(dayTotal)}</span>
                )}
              </div>

              {/* Invoice chips — one per due-date row (a 'separate'-mode
                  invoice with two different due dates this month shows up
                  as two chips, each for just that portion). */}
              {shown.map(row => {
                const isOverdue = row.dueDate < today;
                const isSoon    = !isOverdue && daysFromToday(row.dueDate) >= -7;
                const chipBg    = isOverdue ? '#fef2f2'  : isSoon ? '#fff7ed'  : 'var(--primary-light)';
                const chipColor = isOverdue ? 'var(--danger)' : isSoon ? '#c2410c' : 'var(--primary)';
                const chipBorder= isOverdue ? '#fca5a5'  : isSoon ? '#fdba74'  : 'rgba(67,97,238,0.22)';
                return (
                  <div
                    key={row.key}
                    onClick={() => onSelect(row)}
                    title={`${row.invoice.invoiceNo} — ${entityName(row.invoice.fromEntityId)} — ${money(row.outstanding)} outstanding`}
                    style={{
                      padding: '2px 4px', borderRadius: 3, marginBottom: 2,
                      background: chipBg, color: chipColor,
                      border: `1px solid ${chipBorder}`,
                      fontSize: 9.5, fontWeight: 600, cursor: 'pointer',
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 3,
                      overflow: 'hidden', transition: 'opacity 0.12s',
                    }}
                    onMouseEnter={e => (e.currentTarget.style.opacity = '0.75')}
                    onMouseLeave={e => (e.currentTarget.style.opacity = '1')}
                  >
                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>
                      {entityName(row.invoice.fromEntityId)}
                    </span>
                    <span style={{ flexShrink: 0, opacity: 0.85 }}>
                      {moneyCompact(row.outstanding)}
                    </span>
                  </div>
                );
              })}
              {overflow > 0 && (
                <div style={{ fontSize: 9, color: 'var(--text-3)', paddingLeft: 1, fontWeight: 500 }}>
                  +{overflow}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────

export default function APPage() {
  const { business } = useAuth();
  const settings = useSettings();
  const money = (v: number) => fmtMoney(v, settings.currency);
  const now = new Date();

  const [invoices,       setInvoices]       = useState<PurchaseInvoice[]>([]);
  const [entities,       setEntities]       = useState<Entity[]>([]);
  const [loading,        setLoading]        = useState(true);
  // Paid invoices load too (live), so the Status column filter can show
  // them; the list opens on Unpaid + Partly paid.
  const [paidInvoices,   setPaidInvoices]   = useState<PurchaseInvoice[]>([]);
  const [searchQ,        setSearchQ]        = useState('');
  const [payTarget,      setPayTarget]      = useState<PurchaseInvoice | null>(null);
  const [payGroupId,     setPayGroupId]     = useState<string | undefined>(undefined);
  const [viewMode,       setViewMode]       = useState<'list' | 'calendar'>('list');
  const [calYear,        setCalYear]        = useState(now.getFullYear());
  const [calMonth,       setCalMonth]       = useState(now.getMonth());
  const [detailInvoice,  setDetailInvoice]  = useState<PurchaseInvoice | null>(null);
  const [detailGroupId,  setDetailGroupId]  = useState<string | undefined>(undefined);

  useEffect(() => {
    const db = getClientDb();
    if (!db || !business) return;

    // Single-field `in` filter — no composite index needed.
    // dueDate sorting is done client-side.
    const u1 = onSnapshot(
      query(
        collection(db, 'invoices'),
        where('paymentStatus', 'in', ['unpaid', 'partial']),
      ),
      snap => {
        const rows = snap.docs.map(d => ({ id: d.id, ...d.data() } as PurchaseInvoice));
        rows.sort((a, b) => a.dueDate.localeCompare(b.dueDate));
        setInvoices(rows);
        setLoading(false);
      },
      err => { console.error('AP invoices listener error:', err); setLoading(false); },
    );
    const u2 = onSnapshot(
      collection(db, 'entities'),
      snap => setEntities(snap.docs.map(d => ({ id: d.id, ...d.data() } as Entity))),
      err => console.error('entities listener error:', err),
    );
    const u3 = onSnapshot(
      query(collection(db, 'invoices'), where('paymentStatus', '==', 'paid')),
      snap => setPaidInvoices(snap.docs.map(d => ({ id: d.id, ...d.data() } as PurchaseInvoice))),
      err => console.error('paid invoices listener error:', err),
    );
    return () => { u1(); u2(); u3(); };
  }, [business]);

  function entityName(id: string) {
    return entities.find(e => e.id === id)?.companyName ?? '—';
  }

  const today     = useMemo(() => { const d = new Date(); d.setHours(0,0,0,0); return d; }, []);
  const weekLater = useMemo(() => new Date(today.getTime() + 7 * 86_400_000), [today]);

  // Every invoice flattened to one row per due date — an 'average'-mode
  // invoice is always exactly one row (today's exact shape); a
  // 'separate'-mode invoice contributes one row per DueGroup. Every KPI/
  // filter/list/calendar below reads from this instead of `invoices`
  // directly, so an invoice with multiple due dates shows up once per date.
  const rows = useMemo(() => invoices.flatMap(invoiceToRows), [invoices]);
  const paidRows = useMemo(() => paidInvoices.flatMap(invoiceToRows), [paidInvoices]);

  const totalOutstanding = useMemo(() => rows.reduce((s, r) => s + r.outstanding, 0), [rows]);
  const overdueRows       = useMemo(() => rows.filter(r => new Date(r.dueDate + 'T00:00:00') < today), [rows, today]);
  const overdueAmount    = useMemo(() => overdueRows.reduce((s, r) => s + r.outstanding, 0), [overdueRows]);
  const weekRows          = useMemo(() => rows.filter(r => { const d = new Date(r.dueDate + 'T00:00:00'); return d >= today && d <= weekLater; }), [rows, today, weekLater]);
  const weekAmount       = useMemo(() => weekRows.reduce((s, r) => s + r.outstanding, 0), [weekRows]);

  const baseVisible = useMemo(() => {
    const base = [...rows, ...paidRows];
    const q = searchQ.trim().toLowerCase();
    if (!q) return base;
    return base.filter(r =>
      invoiceNoMatches(r.invoice.invoiceNo, q) ||
      entityName(r.invoice.fromEntityId).toLowerCase().includes(q)
    );
  }, [rows, paidRows, searchQ, entities]); // eslint-disable-line react-hooks/exhaustive-deps

  // Excel-style filters on the header (Invoice No, Supplier, Due Date, Status).
  const filterColumns = useMemo<FilterColumn<ApRow>[]>(() => [
    { key: 'invoice',  label: 'Invoice No', get: r => shortInvoiceNo(r.invoice.invoiceNo) },
    { key: 'supplier', label: 'Supplier',   get: r => entityName(r.invoice.fromEntityId) },
    { key: 'broker',   label: 'Broker',     get: r => invoiceBrokerIds(r.invoice).map(id => entityName(id)) },
    { key: 'due',      label: 'Due Date',   get: r => dueBucket(r), sort: (a, b) => DUE_ORDER.indexOf(a) - DUE_ORDER.indexOf(b) },
    { key: 'status',   label: 'Status',     get: r => statusLabel(r.status) },
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [entities]);
  const { rows: visible, filters: colFilters } = useColumnFilters(baseVisible, filterColumns, { status: DEFAULT_STATUS });
  const paging = usePaging(visible, `${searchQ}|${colFilters.stamp}`);
  const pagedRows = paging.pageRows;
  const listTotals = useMemo(() => {
    const t = { amount: 0, paid: 0, owed: 0, invoices: new Set<string>() };
    for (const r of visible) {
      t.amount += r.amountUSD; t.owed += r.outstanding;
      t.paid += r.group ? r.group.paidUSD : (r.invoice.paidUSD ?? 0);
      t.invoices.add(r.invoice.id);
    }
    return t;
  }, [visible]);
  const wrapRef = useRef<HTMLDivElement>(null);
  useFitHeight(wrapRef, 56, 280, [visible.length > 0, loading, viewMode]);

  function prevCalMonth() {
    if (calMonth === 0) { setCalMonth(11); setCalYear(y => y - 1); }
    else setCalMonth(m => m - 1);
  }
  function nextCalMonth() {
    if (calMonth === 11) { setCalMonth(0); setCalYear(y => y + 1); }
    else setCalMonth(m => m + 1);
  }

  return (
    <AppLayout title="Accounts Payable" subtitle="Outstanding invoice payments"
      toolbar={<>
        <HeaderSearch value={searchQ} onChange={setSearchQ} placeholder="Invoice no, supplier…" label="Search payables" />
        {(searchQ || !onDefaultFilters(colFilters.selected)) && (
          <button type="button" className="tb-clear" onClick={() => { setSearchQ(''); colFilters.clear(); colFilters.set('status', DEFAULT_STATUS); }}>Clear</button>
        )}
      </>}
      headerActions={
        <div className="tb-seg" role="tablist" aria-label="View">
          {(['list', 'calendar'] as const).map(mode => (
            <button key={mode} type="button" role="tab" aria-selected={viewMode === mode}
              className={viewMode === mode ? 'on' : undefined} onClick={() => setViewMode(mode)}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
              {mode === 'list' ? <IconList /> : <IconCalendar />}
              {mode === 'list' ? 'List' : 'Calendar'}
            </button>
          ))}
        </div>
      }
    >

      {/* Figures for everything still owed (whatever the filters say). */}
      {/* Plain figures: the amount, and "None" when there's nothing.
          The invoice count is a small grey note, not mixed into the amount. */}
      <StatChips items={[
        { label: 'To pay', value: <>{money(totalOutstanding)} <span className="tb-stat-note">{invoices.length} invoice{invoices.length !== 1 ? 's' : ''}</span></> },
        { label: 'Overdue', tone: overdueAmount > 0 ? 'danger' : undefined, value: overdueAmount > 0
          ? <>{money(overdueAmount)} <span className="tb-stat-note">{overdueRows.length} payment{overdueRows.length !== 1 ? 's' : ''}</span></>
          : <span className="tb-stat-none">None</span> },
        { label: 'Due this week', tone: weekAmount > 0 ? 'warning' : undefined, value: weekAmount > 0
          ? <>{money(weekAmount)} <span className="tb-stat-note">{weekRows.length} payment{weekRows.length !== 1 ? 's' : ''}</span></>
          : <span className="tb-stat-none">None</span> },
        { label: 'Suppliers', value: String(new Set(invoices.map(i => i.fromEntityId)).size) },
      ]} end={viewMode === 'list' ? <ColumnFilterChips filters={colFilters} inline /> : undefined} />

      {/* ── List view ── */}
      {viewMode === 'list' && (
        loading ? (
          <div className="table-wrap">
            <table><thead><tr>{[120,100,90,80,90,80,75,88].map((w,i) => (
              <th key={i}><div className="skeleton" style={{ height: 11, width: w, borderRadius: 4 }} /></th>
            ))}</tr></thead></table>
          </div>
        ) : visible.length === 0 ? (
          <div className="card" style={{ textAlign: 'center', padding: '48px 24px' }}>
            <div style={{ marginBottom: 12, color: 'var(--success)', display: 'flex', justifyContent: 'center' }}><IconCheckCircle /></div>
            <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-1)', marginBottom: 6 }}>
              {baseVisible.length === 0 ? 'No invoices yet' : onDefaultFilters(colFilters.selected) && !searchQ ? 'All caught up!' : 'Nothing matches these filters'}
            </div>
            <div style={{ fontSize: 13, color: 'var(--text-3)' }}>
              {onDefaultFilters(colFilters.selected) && !searchQ ? 'All invoices have been paid.' : 'Try clearing a filter.'}
            </div>
          </div>
        ) : (
          <>
          <div ref={wrapRef} className="table-wrap table-fit table-fit-stats">
            <table>
              <thead>
                <tr>
                  <th className="th-filterable">Invoice No<ColumnFilterButton filters={colFilters} colKey="invoice" label="Invoice No" /></th>
                  <th className="th-filterable" style={{ width: 190, maxWidth: 190 }}>Supplier<ColumnFilterButton filters={colFilters} colKey="supplier" label="Supplier" /></th>
                  <th className="th-filterable" style={{ width: 150 }}>Broker<ColumnFilterButton filters={colFilters} colKey="broker" label="Broker" /></th>
                  <th style={{ textAlign: 'right' }}>Amount</th>
                  <th style={{ textAlign: 'right', minWidth: 130 }}>Paid</th><th style={{ textAlign: 'right' }}>Outstanding</th>
                  <th>Purchase Date</th>
                  <th className="th-filterable">Due Date<ColumnFilterButton filters={colFilters} colKey="due" label="Due Date" /></th>
                  <th className="th-filterable">Status<ColumnFilterButton filters={colFilters} colKey="status" label="Status" align="right" /></th>
                  <th className="col-actions">Actions</th>
                </tr>
              </thead>
              <tbody>
                {pagedRows.map(row => {
                  const inv     = row.invoice;
                  const owed    = row.outstanding;
                  const paid    = row.group ? row.group.paidUSD : (inv.paidUSD ?? 0);
                  const days    = daysFromToday(row.dueDate);
                  const overdue = days > 0;
                  const dueSoon = !overdue && days >= -7;
                  return (
                    <tr key={row.key} onClick={() => { setDetailInvoice(inv); setDetailGroupId(row.group?.id); }} style={{ cursor: 'pointer' }}>
                      <td>
                        <span
                          title={inv.invoiceNo || undefined}
                          style={{
                            display: 'inline-block', background: 'var(--primary-light)', color: 'var(--primary)',
                            fontFamily: 'var(--font-num)', fontSize: 11, fontWeight: 700,
                            padding: '3px 8px', borderRadius: 6, letterSpacing: '0.02em',
                          }}
                        >{lotNoDisplay(inv.invoiceNo)}</span>
                      </td>
                      <td style={{ maxWidth: 190 }}>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
                          <span className="cell-clip" title={entityName(inv.fromEntityId)} style={{ fontWeight: 500 }}>{entityName(inv.fromEntityId)}</span>
                          {inv.supplierInvoiceNo && (
                            <span style={{ fontSize: 11, color: 'var(--text-3)', fontFamily: 'var(--font-num)' }}>{inv.supplierInvoiceNo}</span>
                          )}
                        </div>
                      </td>
                      <td style={{ fontSize: 12, color: 'var(--text-2)', width: 150, maxWidth: 150 }}>
                        {(() => {
                          const ids = invoiceBrokerIds(inv);
                          return ids.length
                            ? <div style={{ lineHeight: 1.35 }} title={ids.map(id => entityName(id)).join(', ')}>
                                {ids.map(id => <div key={id} className="cell-clip" style={{ maxWidth: 150 }}>{entityName(id)}</div>)}
                              </div>
                            : <span style={{ opacity: 0.4 }}>—</span>;
                        })()}
                      </td>
                      <td style={{ fontFamily: 'var(--font-num)', fontSize: 12, color: 'var(--text-2)', textAlign: 'right' }}>{money(row.amountUSD)}</td>
                      <td style={{ fontFamily: 'var(--font-num)', fontSize: 12, color: 'var(--success)', fontWeight: 600, textAlign: 'right' }}>{paid > 0 ? money(paid) : <span style={{ opacity: 0.4 }}>—</span>}</td>
                      <td style={{ fontFamily: 'var(--font-num)', fontSize: 12, fontWeight: 700, textAlign: 'right', color: overdue ? 'var(--danger)' : dueSoon ? '#c2410c' : 'var(--text-1)' }}>{money(owed)}</td>
                      <td style={{ fontSize: 12, color: 'var(--text-2)', fontFamily: 'var(--font-num)' }}>
                        {inv.purchaseDate
                          ? new Date(inv.purchaseDate + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                          : <span style={{ color: 'var(--text-3)', opacity: 0.5 }}>—</span>
                        }
                      </td>
                      <td>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                          <span style={{ fontSize: 12, fontWeight: 500, color: overdue ? 'var(--danger)' : dueSoon ? '#c2410c' : 'var(--text-1)' }}>
                            {new Date(row.dueDate + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                          </span>
                          <span style={{ fontSize: 10, fontWeight: 600, color: overdue ? 'var(--danger)' : dueSoon ? '#f59e0b' : 'var(--text-3)' }}>
                            {overdue ? `+${days}d over` : days === 0 ? 'Today' : `${Math.abs(days)}d left`}
                          </span>
                        </div>
                      </td>
                      <td><PayBadge inv={{ paymentStatus: row.status, paidUSD: paid, totalAmountUSD: row.amountUSD } as PurchaseInvoice} /></td>
                      <td className="col-actions" onClick={e => e.stopPropagation()}>
                        <div style={{ display: 'flex', gap: 5 }}>
                          <button
                            className="btn btn-primary btn-sm"
                            style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 12, padding: '5px 10px' }}
                            onClick={() => { setPayTarget(inv); setPayGroupId(row.group?.id); }}
                          >
                            <IconPay /> Pay
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr>
                  <td colSpan={3} style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-1)' }}>
                    Total · {listTotals.invoices.size} invoice{listTotals.invoices.size !== 1 ? 's' : ''}
                    {visible.length !== listTotals.invoices.size && <span style={{ color: 'var(--text-3)', fontWeight: 500 }}> ({visible.length} due dates)</span>}
                  </td>
                  <td style={{ fontFamily: 'var(--font-num)', fontSize: 12, fontWeight: 700, color: 'var(--text-1)', textAlign: 'right' }}>{money(listTotals.amount)}</td>
                  <td style={{ fontFamily: 'var(--font-num)', fontSize: 12, fontWeight: 700, color: 'var(--success)', textAlign: 'right' }}>{money(listTotals.paid)}</td>
                  <td style={{ fontFamily: 'var(--font-num)', fontSize: 12, fontWeight: 700, color: 'var(--text-1)', textAlign: 'right' }}>{money(listTotals.owed)}</td>
                  <td colSpan={4} />
                </tr>
              </tfoot>
            </table>
          </div>
          <Paging page={paging.page} totalPages={paging.totalPages} total={paging.total} onChange={paging.setPage} noun="row" />
          </>
        )
      )}

      {/* ── Calendar view ── */}
      {viewMode === 'calendar' && (
        loading ? (
          <div className="card" style={{ padding: '48px', textAlign: 'center', color: 'var(--text-3)' }}>
            Loading invoices…
          </div>
        ) : (
          <CalendarView
            rows={rows}
            entities={entities}
            calYear={calYear}
            calMonth={calMonth}
            onPrevMonth={prevCalMonth}
            onNextMonth={nextCalMonth}
            onSelect={row => { setDetailInvoice(row.invoice); setDetailGroupId(row.group?.id); }}
          />
        )
      )}

      <div style={{ marginTop: 12, fontSize: 12, color: 'var(--text-3)' }}>
        Only invoices created with the invoice model appear here.
      </div>

      <RecordPaymentModal
        invoice={payTarget} onClose={() => { setPayTarget(null); setPayGroupId(undefined); }}
        entityName={payTarget ? entityName(payTarget.fromEntityId) : undefined}
        initialGroupId={payGroupId}
      />

      <InvoiceDetailModal
        invoice={detailInvoice}
        onClose={() => { setDetailInvoice(null); setDetailGroupId(undefined); }}
        onPay={inv => { setDetailInvoice(null); setPayTarget(inv); setPayGroupId(detailGroupId); }}
        entities={entities}
        focusGroupId={detailGroupId}
      />
    </AppLayout>
  );
}
